import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@mantine/core';
import { IconAlertCircle, IconCar, IconHandStop, IconListDetails, IconRefresh, IconShieldCheck } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import {
  RIDES_BOOKING_STATUS,
  RIDES_VERIFICATION_STATUS,
  RIDES_VERIFICATION_TYPE,
  isRidesAvailabilityLive,
  useRidesAvailabilityService,
  useRidesBookingsService,
  type RidesAvailabilityView,
  type RidesBookingView,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { findVerification, useDriverProfile, useMeSummary, useMyVerifications } from '@/lib/me';
import { campusById, useRidesLookups } from '@/lib/lookups';
import { photoRules, useCopy, visiblePhoto } from '@/lib/settings';
import { ridesKeys } from '@/lib/queryKeys';
import { isPastDate } from '@/lib/format';
import { ResumeCard } from '@/components/rides/ResumeCard';
import { PushOptInCard, usePushOptIn } from '@/components/rides/PushOptInCard';
import { InstallHint } from '@/components/rides/InstallHint';
import { PhotoNudgeCard, usePhotoNudge } from '@/components/rides/PhotoNudgeCard';
import { DocumentsNudgeCard, useDocumentsNudge } from '@/components/rides/DocumentsNudgeCard';
import { ActionCard, Callout, ErrorState, SkeletonCard } from '@/components/ui';
import { NudgeCard } from '@/components/member/NudgeCard';
import { BookingRow, LiftRow, NextLiftCard, NextRideCard, PastRow, WelcomeArt } from '@/components/member/TripCards';
import { tripMoment } from '@/components/member/trips';
import { useRidesWizard } from '@/store/ridesWizard';
import '@/styles/member.css';

/** "Good morning" / "Good afternoon" / "Good evening" for the member's local time. */
function greetingFor(d: Date): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

type Hero = { kind: 'ride'; booking: RidesBookingView } | { kind: 'lift'; listing: RidesAvailabilityView } | null;

/** The soonest of the next booked ride and the next lift the member is driving. */
function pickHero(next: RidesBookingView | null | undefined, lift: RidesAvailabilityView | null | undefined): Hero {
  if (next && lift) return tripMoment(lift) < tripMoment(next) ? { kind: 'lift', listing: lift } : { kind: 'ride', booking: next };
  if (next) return { kind: 'ride', booking: next };
  if (lift) return { kind: 'lift', listing: lift };
  return null;
}

/**
 * Home: one clear "what's next" (the next ride or lift, or the two ways to start), then at most one
 * thing to do (documents, driver checks, photo, alerts, in that order), then what's coming up and
 * the last few rides. During maintenance the booked rides stay readable; only new bookings and
 * changes are paused.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const { t, settings } = useCopy();
  const { getUserDisplayName } = useEnrichedAuth();
  const setRole = useRidesWizard((s) => s.setRole);
  const lookups = useRidesLookups();
  const summary = useMeSummary();
  const driver = useDriverProfile();
  const bookingsSvc = useRidesBookingsService();
  const availabilitySvc = useRidesAvailabilityService();
  const bookings = useQuery({ queryKey: ridesKeys.bookings.mine, queryFn: () => bookingsSvc.getMine(), staleTime: 30_000 });
  const listings = useQuery({ queryKey: ridesKeys.availability.mine, queryFn: () => availabilitySvc.getMine(), staleTime: 30_000 });

  const maintenance = settings.maintenanceMode;
  const riderOpen = settings.riderRegistrationOpen && !maintenance;
  const driverOpen = settings.driverRegistrationOpen && !maintenance;
  const isDriver = !!driver.data || !!summary.data?.hasDriverProfile;
  const photos = photoRules(settings);
  const first = getUserDisplayName().split(' ')[0];

  const hero = pickHero(summary.data?.nextBooking, summary.data?.nextListing);
  const heroBookingId = hero?.kind === 'ride' ? hero.booking.rideBookingID : null;
  const heroListingId = hero?.kind === 'lift' ? hero.listing.rideAvailabilityID : null;

  // ---- what's coming up (the hero trip is not repeated) and the last few rides
  const allBookings = bookings.data ?? [];
  const upcomingBookings = allBookings.filter(
    (b) => !isPastDate(b.date) && (b.statusID === RIDES_BOOKING_STATUS.Confirmed || b.statusID === RIDES_BOOKING_STATUS.CancelledByDriver) && b.rideBookingID !== heroBookingId,
  );
  const pastBookings = allBookings
    .filter((b) => !upcomingBookings.includes(b) && b.rideBookingID !== heroBookingId && (isPastDate(b.date) || b.statusID !== RIDES_BOOKING_STATUS.Confirmed))
    .sort((a, b) => tripMoment(b) - tripMoment(a))
    .slice(0, 3);
  const upcomingLifts = (listings.data ?? []).filter((l) => !isPastDate(l.date) && isRidesAvailabilityLive(l.statusID) && l.rideAvailabilityID !== heroListingId);
  const upcoming = [
    ...upcomingBookings.map((b) => ({ key: `b${b.rideBookingID}`, at: tripMoment(b), node: <BookingRow key={`b${b.rideBookingID}`} booking={b} interactive={!maintenance} /> })),
    ...upcomingLifts.map((l) => ({ key: `l${l.rideAvailabilityID}`, at: tripMoment(l), node: <LiftRow key={`l${l.rideAvailabilityID}`} listing={l} interactive={!maintenance} /> })),
  ].sort((a, b) => a.at - b.at);
  const listsFailed = (bookings.isError || listings.isError) && !summary.isError;

  // ---- one thing to do, most important first
  const docsNudge = useDocumentsNudge(isDriver);
  const photoNudge = usePhotoNudge(isDriver);
  const push = usePushOptIn();
  const checksNudge = useDriverChecksNudge(isDriver && !driver.data?.isFullyVerified);
  let nudge: JSX.Element | null = null;
  if (!maintenance) {
    if (docsNudge.show) nudge = <DocumentsNudgeCard isDriver={isDriver} />;
    else if (checksNudge) {
      nudge = (
        <NudgeCard
          tone={checksNudge === 'failed' ? 'bad' : 'warn'}
          icon={checksNudge === 'failed' ? <IconAlertCircle size={24} /> : <IconShieldCheck size={24} />}
          title={checksNudge === 'failed' ? "A driver check didn't pass" : 'Finish your driver checks'}
          action={
            settings.driverRegistrationOpen
              ? <Button component={Link} to="/drive/verify" variant="default">See my checks</Button>
              : <Button component={Link} to="/account#checks" variant="default">See my checks</Button>
          }
        >
          {checksNudge === 'failed' ? t('home.checks_failed') : t('home.checks_nudge')}
        </NudgeCard>
      );
    } else if (photoNudge.show) nudge = <PhotoNudgeCard isDriver={isDriver} onDismiss={photoNudge.dismiss} />;
    else if (push.suggest) nudge = <PushOptInCard onDismiss={push.dismiss} />;
  }

  const startRide = () => { setRole('rider'); navigate('/ride/event'); };
  const startLift = () => { setRole('driver'); navigate(driver.data ? '/drive/events' : '/drive/profile'); };
  const retryAll = () => { summary.refetch(); bookings.refetch(); listings.refetch(); };

  const lede = summary.isSuccess && !maintenance
    ? hero ? t('home.lede_next') : riderOpen || driverOpen ? undefined : t('home.closed')
    : undefined;
  const welcome = summary.isSuccess && !hero && (riderOpen || driverOpen);

  const actions = (
    <div className="rm-stack">
      {riderOpen && (
        <ActionCard
          primary={!hero}
          icon={<IconHandStop size={26} />}
          title={hero ? 'Book another ride' : 'Find a ride'}
          description="Get a lift to a service or event."
          onClick={startRide}
        />
      )}
      {driverOpen && (
        <ActionCard icon={<IconCar size={26} />} title="Offer a lift" description="Share your spare seats with others going to church." onClick={startLift} />
      )}
    </div>
  );
  const closedNote = (
    <>
      {!riderOpen && <p className="rm-small" style={{ marginTop: 8 }}>{t('home.rider_closed')}</p>}
      {!driverOpen && <p className="rm-small" style={{ marginTop: 8 }}>{t('home.driver_closed')}</p>}
    </>
  );

  return (
    <div className="rides-main">
      <header className="rm-greet">
        <h1 className="hello">{first ? `${greetingFor(new Date())}, ${first}` : greetingFor(new Date())}</h1>
        {lede && <p>{lede}</p>}
      </header>

      <div className="rm-stack">
        {maintenance && <Callout tone="warn" title="Taking a short break">{t('home.maintenance')}</Callout>}

        {summary.isLoading ? (
          <div role="status" aria-live="polite" aria-busy="true">
            <span className="sr-only">Loading your rides…</span>
            <SkeletonCard lines={4} />
          </div>
        ) : summary.isError ? (
          <ErrorState title="We couldn't load your rides" message={t('home.load_error')} onRetry={retryAll} retrying={summary.isRefetching} />
        ) : hero?.kind === 'ride' ? (
          <NextRideCard
            booking={hero.booking}
            driverPhotoUrl={visiblePhoto(hero.booking.driverPhotoUrl, photos.driverToRiders)}
            campus={campusById(lookups.data, hero.booking.collectionLocationID)}
            interactive={!maintenance}
          />
        ) : hero?.kind === 'lift' ? (
          <NextLiftCard listing={hero.listing} campus={campusById(lookups.data, hero.listing.collectionLocationID)} interactive={!maintenance} />
        ) : welcome ? (
          <section className="rm-welcome rm-rise" aria-labelledby="rm-welcome-title">
            <WelcomeArt />
            <h2 id="rm-welcome-title">{t('home.welcome_title')}</h2>
            <p>{t('home.lede_empty')}</p>
            {actions}
            {closedNote}
          </section>
        ) : null}

        {!maintenance && <ResumeCard />}
        {hero && nudge}
      </div>

      {(riderOpen || driverOpen) && !summary.isLoading && !welcome && (
        <section className={`rm-section rm-rise d1${hero || summary.isError ? '' : ' first'}`} aria-labelledby="rm-start-title">
          <h2 id="rm-start-title" className={hero ? undefined : 'sr-only'}>{hero ? 'Plan another trip' : 'Start a trip'}</h2>
          {actions}
          {closedNote}
        </section>
      )}
      {!maintenance && !riderOpen && !driverOpen && hero && (
        <div className="rm-section"><Callout tone="info">{t('home.closed')}</Callout></div>
      )}
      {/* Nothing booked: the ways to start come first, then the one thing to do. */}
      {!hero && nudge && <div className="rm-section rm-rise d1">{nudge}</div>}

      {listsFailed && (
        <div className="rm-section">
          <Callout
            tone="bad"
            action={<Button variant="default" leftSection={<IconRefresh size={18} />} onClick={() => { bookings.refetch(); listings.refetch(); }}>Try again</Button>}
          >
            {t('home.list_error')}
          </Callout>
        </div>
      )}

      {upcoming.length > 0 && (
        <section className="rm-section" aria-labelledby="rm-upcoming-title">
          <h2 id="rm-upcoming-title">Coming up</h2>
          <div className="rm-rows">{upcoming.map((u) => u.node)}</div>
        </section>
      )}

      {isDriver && !maintenance && (
        <div style={{ marginTop: 12 }}>
          <Button variant="subtle" fullWidth component={Link} to="/drive/listings" leftSection={<IconListDetails size={20} />}>
            See all my lifts
          </Button>
        </div>
      )}

      {pastBookings.length > 0 && (
        <section className="rm-section" aria-labelledby="rm-past-title">
          <h2 id="rm-past-title">Past rides</h2>
          <div className="rm-rows">{pastBookings.map((b) => <PastRow key={b.rideBookingID} booking={b} interactive={!maintenance} />)}</div>
        </section>
      )}

      {!maintenance && !nudge && summary.isSuccess && (
        <div className="rm-section"><InstallHint /></div>
      )}

    </div>
  );
}

/**
 * Drivers whose ID or licence check isn't done: 'failed' when one didn't pass, 'todo' when one is
 * missing or waiting, null otherwise. Only decided once the checks have loaded.
 */
function useDriverChecksNudge(enabled: boolean): 'failed' | 'todo' | null {
  const verifications = useMyVerifications();
  if (!enabled || !verifications.isSuccess) return null;
  const list = [
    findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GovernmentId),
    findVerification(verifications.data, RIDES_VERIFICATION_TYPE.DriversLicence),
  ];
  const bad = [RIDES_VERIFICATION_STATUS.Failed, RIDES_VERIFICATION_STATUS.Expired, RIDES_VERIFICATION_STATUS.Revoked] as number[];
  if (list.some((v) => v && bad.includes(v.statusID))) return 'failed';
  if (list.some((v) => !v || v.statusID !== RIDES_VERIFICATION_STATUS.Verified)) return 'todo';
  return null;
}
